/**
 * Password strength policy — the browser half.
 *
 * User feedback (Richard, 2026-07-28): "Password must not be simple." He was
 * right: signup and reset both checked `length < 8` and nothing else, so
 * `password`, `12345678` and `aaaaaaaa` all sailed through.
 *
 * THIS FILE IS THE ENFORCEMENT ON THE SIGNUP AND RESET PATHS. That is unusual
 * and worth understanding before you "move it to the backend":
 *
 *   - Signup calls `authClient.signUp()` — browser straight to GoTrue. Our API
 *     is not in the call path. (The backend's `/api/public/signup` route exists
 *     but was rolled back on 2026-05-13 for dropping confirmation emails; see
 *     the long comment in LoginPage.tsx.)
 *   - Reset calls `authClient.updateUser({ password })` — also browser to
 *     GoTrue directly.
 *
 * So a determined person can bypass this by talking to GoTrue themselves. That
 * is fine, because of what the rule is actually FOR: stopping someone from
 * wandering into a guessable password on their own account. Nobody attacks
 * themselves. The backend keeps a matching copy in
 * `backend/services/password_policy.py` so any route that ever does accept a
 * password is covered — `backend/tests/test_password_policy.py` reads this
 * file and fails if the two rule sets drift apart.
 *
 * WHAT THIS DELIBERATELY DOES NOT DO: demand an uppercase, a number and a
 * symbol. Those rules are what produce `Password1!` — the most predictable
 * possible mutation, and attackers know it. NIST SP 800-63B recommends against
 * composition rules and for blocklists of known-weak choices, which is this.
 */

export const MIN_LENGTH = 8

/**
 * The passwords that actually turn up in breach dumps, plus the ones people
 * reach for when an app says "8 characters". Not exhaustive — a real list runs
 * to hundreds of thousands of entries and belongs in a service, not a
 * constant. This catches the realistic accidental choice, which is the risk.
 *
 * Mirrored exactly in backend/services/password_policy.py. If you add one
 * here, add it there — a backend test compares the two lists character for
 * character and will fail the build otherwise.
 */
export const COMMON_PASSWORDS: ReadonlySet<string> = new Set([
  'password', 'passw0rd', 'password1', 'password12', 'password123',
  '12345678', '123456789', '1234567890', '87654321', '11111111',
  'qwertyui', 'qwerty123', 'qwertyuiop', 'asdfghjk', 'asdfghjkl',
  'iloveyou', 'sunshine', 'princess', 'football', 'baseball',
  'welcome1', 'welcome123', 'letmein1', 'letmein123', 'admin123',
  'trustno1', 'starwars', 'superman', 'batman123', 'michael1',
  'monkey12', 'dragon123', 'abc12345', 'a1b2c3d4', 'changeme',
  // App-specific: what a TrybeUP user would reach for first.
  'trybeup1', 'trybeup123', 'trybeup2026', 'habits123', 'fitness1',
])

function isSingleRepeatedChar(pw: string): boolean {
  return new Set(pw.split('')).size === 1
}

/** Runs like 12345678 / abcdefgh, forwards or backwards. */
function isSequential(pw: string): boolean {
  const lowered = pw.toLowerCase()
  if (lowered.length < 4) return false
  let ascending = true
  let descending = true
  for (let i = 0; i < lowered.length - 1; i++) {
    const step = lowered.charCodeAt(i + 1) - lowered.charCodeAt(i)
    if (step !== 1) ascending = false
    if (step !== -1) descending = false
  }
  return ascending || descending
}

/**
 * Word-ish chunks of an email or name, for the "don't put your own details in
 * it" check. Anything under 3 characters is ignored so a name like "Jo"
 * doesn't ban every password containing "jo".
 */
function tokens(value: string | null | undefined): string[] {
  if (!value) return []
  const local = value.split('@')[0]
  return local
    .split(/[^a-zA-Z0-9]+/)
    .filter((p) => p.length >= 3)
    .map((p) => p.toLowerCase())
}

/**
 * Returns a user-facing error message, or null if the password is fine.
 *
 * Messages say what to do rather than what's wrong ("pick something else"
 * beats "invalid password"), because the person reading it is trying to sign
 * up and every second spent guessing our rules is a second they might not
 * spend.
 */
export function validatePassword(
  password: string,
  opts: { email?: string | null; name?: string | null } = {},
): string | null {
  if (!password) return 'Please choose a password.'

  // Don't trim: leading and trailing spaces are legitimate password characters.
  if (password.length < MIN_LENGTH) {
    return `Password must be at least ${MIN_LENGTH} characters.`
  }

  const lowered = password.toLowerCase()

  if (COMMON_PASSWORDS.has(lowered)) {
    return 'That password is one of the most common ones — please pick something else.'
  }

  if (isSingleRepeatedChar(password)) {
    return "That's the same character repeated — please pick something else."
  }

  if (isSequential(password)) {
    return "That's a simple sequence like 12345678 — please pick something else."
  }

  // All digits is a date of birth or a phone number often enough to matter.
  if (/^\d+$/.test(password)) {
    return 'Numbers only is easy to guess — add some letters.'
  }

  for (const token of [...tokens(opts.email), ...tokens(opts.name)]) {
    if (lowered.includes(token)) {
      return "Please don't use your name or email in your password."
    }
  }

  return null
}
